import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronRight,
  FileText,
  Layers,
  MessageCircle,
  MousePointerClick,
  Rocket,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";
import Footer from "../../components/footer/Footer";
import { INDUSTRIES } from "../../data/categoryData";
import { useCategories } from "../../contexts/CategoriesContext";

const PROVIDER_LOGIN_URL = "https://provider.infoboxx.com/login";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut", delay },
});

const partnerStats = [
  { value: "6", label: "high-intent category hubs" },
  { value: "60+", label: "sponsorly topic lanes" },
  { value: "24/7", label: "content-led discovery" },
  { value: "SMS", label: "plus web retargeting" },
];

const partnerBenefits = [
  {
    icon: Target,
    title: "Reach people before they choose",
    text: "InfoBoxx captures users while they are still researching, comparing, and asking high-intent questions.",
  },
  {
    icon: BookOpen,
    title: "Educate before you sell",
    text: "Sponsor helpful guides, FAQs, explainers, and decision tools that build trust before a sales conversation starts.",
  },
  {
    icon: Users,
    title: "Receive qualified leads",
    text: "Users arrive with context. They have read, compared, clicked, asked, and signaled the category they care about.",
  },
  {
    icon: BarChart3,
    title: "Measure the journey",
    text: "Track interest by category, topic, content engagement, call requests, WhatsApp clicks, and appointment intent.",
  },
];

const partnerModel = [
  {
    icon: Layers,
    title: "Category Sponsorship",
    text: "Become the trusted provider behind a full category or a selected subcategory lane.",
  },
  {
    icon: FileText,
    title: "Expert Content Ownership",
    text: "Publish practical articles, explainers, checklists, and service pages users can trust.",
  },
  {
    icon: MousePointerClick,
    title: "Conversion Actions",
    text: "Turn traffic into leads through forms, WhatsApp, callback requests, and bookings.",
  },
  {
    icon: TrendingUp,
    title: "Lead Intelligence",
    text: "Understand what users explored before they contacted you, so follow-up starts warmer.",
  },
];

const onboardingSteps = [
  {
    number: "01",
    title: "Register Your Business",
    shortTitle: "Register",
    icon: Building2,
    summary: "Enter your company and contact details",
    detail:
      "Create your partner account with company information, address, contact person, and your preferred lead handling channel.",
    items: ["Company name", "Business address", "Primary contact", "Lead channel preference"],
  },
  {
    number: "02",
    title: "Verify Your Business",
    shortTitle: "Verify",
    icon: BadgeCheck,
    summary: "Upload required documents for vetting",
    detail:
      "Our team reviews your business documents to keep the platform credible and protect users from unverified providers.",
    items: ["Certificate of Incorporation", "TIN", "Business Registration"],
  },
  {
    number: "03",
    title: "Select Your Category",
    shortTitle: "Category",
    icon: Layers,
    summary: "Choose the verticals you want to sponsor",
    detail:
      "Pick the categories and topic lanes that match your services, from Legal and Health to Real Estate and Careers.",
    items: [],
  },
  {
    number: "04",
    title: "Set Up Your Profile",
    shortTitle: "Profile",
    icon: UserCheck,
    summary: "Add services, content, and conversion paths",
    detail:
      "Build your provider profile with service details, expert content, FAQs, offers, and the actions users can take.",
    items: ["Service descriptions", "Guides and FAQs", "Contact options", "Booking preferences"],
  },
  {
    number: "05",
    title: "Get Approved & Go Live",
    shortTitle: "Go Live",
    icon: Rocket,
    summary: "Start receiving qualified leads",
    detail:
      "Once approved, your profile and sponsored content go live inside relevant InfoBoxx journeys where users are already looking for answers.",
    items: ["Profile approval", "Content publishing", "Lead routing", "Performance tracking"],
  },
];

const leadJourney = [
  "A user lands with a real question",
  "They read structured, helpful content",
  "They compare options and understand the decision",
  "They contact, book, call, or submit a lead form",
  "Your team follows up with context",
];

const faqs = [
  {
    q: "Who can become an InfoBoxx partner?",
    a: "Registered businesses, service providers, professional firms, agencies, clinics, advisors, and category experts who can support users with real services.",
  },
  {
    q: "Do partners need to create content?",
    a: "Partners can provide content, collaborate with the InfoBoxx team, or sponsor content we produce. The goal is to educate users before asking them to convert.",
  },
  {
    q: "How are leads delivered?",
    a: "Leads can be routed through forms, WhatsApp, callback requests, calls, and appointment bookings depending on your selected setup.",
  },
  {
    q: "Can one partner sponsor multiple categories?",
    a: "Yes. Partners can apply for multiple categories or subcategories if their business is verified and relevant to those services.",
  },
];

const PartnersPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const { industries: apiIndustries } = useCategories();
  const industries = apiIndustries.length ? apiIndustries : INDUSTRIES;
  const onboardingStepsResolved = useMemo(
    () =>
      onboardingSteps.map((entry) =>
        entry.number === "03"
          ? { ...entry, items: industries.map((industry) => industry.label) }
          : entry,
      ),
    [industries],
  );
  const step = onboardingStepsResolved[activeStep];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f4ec] [font-family:'Onest',sans-serif]">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#f7f4ec] px-6 pb-24 pt-32 text-black lg:px-12">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -left-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-[#FFD221]/25 blur-[120px]" />
          <div className="absolute right-[-10rem] top-20 h-[30rem] w-[30rem] rounded-full bg-white blur-[90px]" />
          <div className="absolute bottom-0 left-1/2 h-56 w-[80vw] -translate-x-1/2 rounded-full bg-[#FFD221]/10 blur-[90px]" />
          <div
            className="absolute inset-0 opacity-[0.42]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(0,0,0,.11) 1px, transparent 0)",
              backgroundSize: "34px 34px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <motion.span
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-black shadow-sm"
              >
                <Sparkles size={14} />
                Partner growth program
              </motion.span>

              <motion.h1
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08, duration: 0.6 }}
                className="mt-7 max-w-4xl text-5xl font-black leading-[1.02] tracking-tight text-black sm:text-6xl lg:text-[4.8rem]"
              >
                Own the moment before your customer{" "}
                <span className="inline-block relative">
                  <span className="bg-gradient-to-r from-[#b8860b] via-[#FFD221] to-[#c9a000] bg-clip-text text-transparent">
                    decides
                  </span>
                  <svg
                    className="absolute left-0 -bottom-2 w-full h-3"
                    viewBox="0 0 220 12"
                    fill="none"
                    preserveAspectRatio="none"
                    aria-hidden
                  >
                    <path
                      d="M3 8C58 2 158 2 217 8"
                      stroke="#FFD221"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                .
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.16, duration: 0.55 }}
                className="mt-7 max-w-2xl text-lg leading-relaxed text-gray-650"
              >
                Become the verified provider users meet while they are asking,
                learning, comparing, and preparing to act. InfoBoxx turns trust
                into content, content into intent, and intent into qualified
                partner leads.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.24, duration: 0.5 }}
                className="mt-9 flex flex-nowrap items-stretch gap-2 sm:gap-3"
              >
                <Link
                  to="/partners/onboard"
                  className="group inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-2xl bg-black px-3 py-3.5 text-center text-[11px] font-black leading-snug text-white shadow-[0_22px_54px_-26px_rgba(0,0,0,0.65)] transition hover:bg-[#FFD221] hover:text-black sm:gap-2 sm:px-6 sm:py-4 sm:text-sm"
                >
                  <span className="truncate sm:whitespace-normal">Start Partner Onboarding</span>
                  <Rocket size={15} className="hidden shrink-0 transition group-hover:translate-x-0.5 sm:inline" />
                </Link>
                <a
                  href={PROVIDER_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-2xl bg-[#FFD221] px-3 py-3.5 text-center text-[11px] font-black leading-snug text-black shadow-[0_18px_44px_-24px_rgba(201,160,0,0.9)] transition hover:bg-[#e6bc00] sm:gap-2 sm:px-6 sm:py-4 sm:text-sm"
                >
                  <span className="truncate sm:whitespace-normal">Partner Sign In</span>
                  <ArrowRight size={14} className="hidden shrink-0 sm:inline" />
                </a>
                <Link
                  to="/contact"
                  className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-2xl border border-black/10 bg-white px-3 py-3.5 text-center text-[11px] font-black leading-snug text-black shadow-sm transition hover:border-black/25 hover:shadow-md sm:gap-2 sm:px-6 sm:py-4 sm:text-sm"
                >
                  <span className="truncate sm:whitespace-normal">Talk to Us</span>
                  <MessageCircle size={14} className="hidden shrink-0 sm:inline" />
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32, duration: 0.5 }}
                className="grid grid-cols-2 gap-3 mt-10 max-w-2xl sm:grid-cols-4"
              >
                {partnerStats.map(({ value, label }) => (
                  <div
                    key={label}
                    className="rounded-2xl border border-black/[0.07] bg-white p-4 shadow-sm"
                  >
                    <p className="text-2xl font-black text-black">{value}</p>
                    <p className="mt-1 text-[11px] leading-snug text-gray-500">{label}</p>
                  </div>
                ))}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.65 }}
              className="relative"
            >
              <div className="absolute -left-4 top-10 hidden h-28 w-28 rounded-[2rem] bg-[#FFD221] lg:block" />
              <div className="absolute -right-6 bottom-12 hidden h-32 w-32 rounded-full border-[20px] border-white lg:block" />

              <div className="relative overflow-hidden rounded-[2.5rem] border border-black/[0.08] bg-gray-950 p-4 text-white shadow-[0_40px_100px_-50px_rgba(0,0,0,0.7)]">
                <div className="absolute right-0 top-0 h-60 w-60 rounded-full bg-[#FFD221]/15 blur-3xl" />
                <div className="relative rounded-[2rem] border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
                  <div className="flex gap-4 justify-between items-center mb-5">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#FFD221]">
                        Partner command center
                      </p>
                      <p className="mt-1 text-xl font-black">Onboarding preview</p>
                    </div>
                    <Link
                      to="/partners#how-to-become-a-partner"
                      className="hidden rounded-xl bg-[#FFD221] px-4 py-2 text-xs font-black text-black transition hover:bg-[#ffdf44] sm:inline-flex"
                    >
                      Onboard now
                    </Link>
                  </div>

                  <div className="grid gap-4 md:grid-cols-[0.85fr_1.15fr]">
                    <div className="overflow-hidden rounded-3xl">
                      <img
                        src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=700&q=85"
                        alt="Partners discussing growth strategy"
                        className="h-full min-h-[330px] w-full object-cover"
                      />
                    </div>

                    <div className="space-y-3">
                      {onboardingSteps.slice(0, 4).map(({ number, shortTitle, icon: StepIcon }, index) => (
                        <div
                          key={number}
                          className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${
                            index === 0
                              ? "border-[#FFD221]/40 bg-[#FFD221] text-black"
                              : "border-white/10 bg-white/[0.06] text-white"
                          }`}
                        >
                          <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                              index === 0 ? "bg-black/10" : "bg-white/10"
                            }`}
                          >
                            <StepIcon size={16} />
                          </span>
                          <div>
                            <p className={`text-[10px] font-black uppercase tracking-widest ${index === 0 ? "text-black/55" : "text-gray-500"}`}>
                              Step {number}
                            </p>
                            <p className="text-sm font-black">{shortTitle}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-3 mt-5 sm:grid-cols-3">
                    {[
                      { icon: Target, label: "Intent", value: "High" },
                      { icon: ShieldCheck, label: "Trust", value: "Verified" },
                      { icon: Zap, label: "Lead flow", value: "Active" },
                    ].map(({ icon: Icon, label, value }) => (
                      <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                        <Icon size={17} className="text-[#FFD221]" />
                        <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-gray-500">
                          {label}
                        </p>
                        <p className="mt-1 text-sm font-black text-white">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-2 bottom-10 hidden max-w-[230px] rounded-3xl border border-black/[0.08] bg-white p-4 text-black shadow-2xl md:block lg:-left-10"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFD221]">
                  <Users size={18} />
                </div>
                <p className="text-sm font-black">Warm prospects</p>
                <p className="mt-1 text-xs leading-relaxed text-gray-500">
                  Users learn first, then reach out with clearer intent.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="px-6 py-24 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp(0)} className="mb-14 max-w-3xl">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
              Why Partner With Us
            </p>
            <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
              Built for providers who want intent, not noise.
            </h2>
          </motion.div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {partnerBenefits.map(({ icon: Icon, title, text }, index) => (
              <motion.article
                key={title}
                {...fadeUp(index * 0.08)}
                whileHover={{ y: -6 }}
                className="rounded-3xl border border-black/[0.08] bg-white p-6 shadow-sm transition hover:border-[#FFD221]/60 hover:shadow-xl"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFD221] text-black">
                  <Icon size={22} />
                </div>
                <h3 className="text-lg font-black text-black">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-500">{text}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Partner model */}
      <section className="px-6 py-24 bg-white lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <motion.div {...fadeUp(0)}>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
              The Partner Model
            </p>
            <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
              From expertise to measurable demand.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-gray-600">
              Instead of placing your brand beside random traffic, InfoBoxx
              places your expertise inside structured learning journeys. Users
              understand their need, trust the context, and then choose the
              right next step.
            </p>
            <Link
              to="/partners/onboard"
              className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-[#FFD221] px-7 py-3.5 text-sm font-black text-black shadow-sm transition hover:bg-[#e6bc00]"
            >
              Start Your Application
              <ArrowRight size={16} />
            </Link>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-2">
            {partnerModel.map(({ icon: Icon, title, text }, index) => (
              <motion.div
                key={title}
                {...fadeUp(index * 0.08)}
                className={`rounded-3xl border p-6 ${
                  index === 0
                    ? "border-[#FFD221]/50 bg-gray-950 text-white"
                    : "border-black/[0.08] bg-[#f7f4ec] text-black"
                }`}
              >
                <div
                  className={`mb-5 flex h-11 w-11 items-center justify-center rounded-2xl ${
                    index === 0 ? "bg-[#FFD221] text-black" : "bg-white text-[#c9a000]"
                  }`}
                >
                  <Icon size={20} />
                </div>
                <h3 className={`font-black ${index === 0 ? "text-white" : "text-black"}`}>{title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${index === 0 ? "text-gray-400" : "text-gray-500"}`}>
                  {text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 py-24 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp(0)} className="flex flex-col gap-5 mb-12 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
                Sponsorship Lanes
              </p>
              <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
                Choose your category lane.
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-gray-600">
              Start with one category, own a subcategory, or expand across
              multiple decision journeys as your content and leads grow.
            </p>
          </motion.div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {industries.map((industry, index) => (
              <motion.div key={industry.id} {...fadeUp(index * 0.06)}>
                <Link
                  to={industry.path || `/${industry.id}`}
                  className="group flex h-full flex-col rounded-3xl border border-black/[0.08] bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#FFD221]/70 hover:shadow-xl"
                >
                  <div className="flex gap-4 justify-between items-start mb-6">
                    <span className="rounded-full bg-[#FFD221]/20 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[#9a7b00]">
                      {industry.itemCount} topic lanes
                    </span>
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#f7f4ec] text-black transition group-hover:bg-[#FFD221]">
                      <ChevronRight size={18} />
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-black">{industry.label}</h3>
                  <p className="flex-1 mt-3 text-sm leading-relaxed text-gray-500">
                    {industry.description}
                  </p>
                  <span className="inline-flex gap-2 items-center mt-6 text-sm font-black text-black">
                    View category
                    <ArrowRight size={15} className="transition group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Onboarding */}
      <section id="how-to-become-a-partner" className="overflow-hidden relative px-6 py-24 bg-white scroll-mt-24 lg:px-12">
        <div className="pointer-events-none absolute right-0 top-0 h-[26rem] w-[26rem] rounded-full bg-[#FFD221]/10 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl">
          <motion.div {...fadeUp(0)} className="mx-auto mb-14 max-w-3xl text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/15 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[#9a7b00] shadow-sm">
              <Sparkles size={14} />
              Partner onboarding
            </span>
            <h2 className="text-4xl font-black leading-tight text-gray-950 sm:text-5xl">
              How to become a partner
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-gray-500">
              A polished 6-step path from company information to verified category
              presence and qualified lead flow.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-[22rem_1fr] lg:items-start">
            <div className="flex overflow-x-auto relative gap-2 pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
              <div className="pointer-events-none absolute left-[1.55rem] top-6 hidden h-[calc(100%-3rem)] w-px bg-gradient-to-b from-[#FFD221]/70 via-[#FFD221]/30 to-transparent lg:block" />
              {onboardingStepsResolved.map((item, index) => {
                const StepIcon = item.icon;
                const selected = index === activeStep;
                const done = index < activeStep;
                return (
                  <motion.button
                    key={item.number}
                    type="button"
                    onClick={() => setActiveStep(index)}
                    whileHover={{ x: selected ? 0 : 3 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative flex min-w-[10rem] items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all lg:min-w-0 ${
                      selected
                        ? "border-[#FFD221] bg-[#FFD221] shadow-[0_16px_50px_-24px_rgba(255,210,33,0.75)]"
                        : done
                        ? "border-black/10 bg-[#f7f4ec]"
                        : "border-black/[0.08] bg-white hover:border-black/15"
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${
                        selected
                          ? "bg-black/10 text-black"
                          : done
                          ? "bg-[#FFD221]/50 text-[#9a7b00]"
                          : "bg-[#f7f4ec] text-gray-500"
                      }`}
                    >
                      {done ? <CheckCircle2 size={17} /> : <StepIcon size={16} />}
                    </span>
                    <span className="min-w-0">
                      <span className={`block text-[10px] font-black uppercase tracking-[0.18em] ${selected ? "text-black/60" : "text-gray-400"}`}>
                        Step {item.number}
                      </span>
                      <span className="block text-sm font-black text-black truncate">{item.shortTitle}</span>
                    </span>
                  </motion.button>
                );
              })}
            </div>

            <div className="min-h-[28rem]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, x: 18, scale: 0.98 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -12, scale: 0.99 }}
                  transition={{ duration: 0.32, ease: "easeOut" }}
                  className="overflow-hidden rounded-[2rem] border border-black/[0.08] bg-[#f7f4ec] shadow-[0_24px_80px_-48px_rgba(0,0,0,0.45)]"
                >
                  <div className="h-1.5 w-full bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-[#ffec80]" />
                  <div className="p-6 sm:p-8 lg:p-10">
                    <div className="flex gap-4 justify-between items-start">
                      <div className="flex gap-4 items-center">
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#FFD221] text-black shadow-lg ring-2 ring-black/5">
                          <step.icon size={30} />
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#c9a000]">
                            Step {step.number}
                          </p>
                          <h3 className="text-2xl font-black text-gray-950">{step.title}</h3>
                        </div>
                      </div>
                      <span className="text-[3rem] font-black leading-none text-black/[0.05]" aria-hidden>
                        {step.number}
                      </span>
                    </div>

                    <p className="mt-8 inline-flex w-fit items-center gap-2 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/15 px-4 py-2 text-sm font-semibold text-gray-800">
                      <Briefcase size={14} className="text-[#c9a000]" />
                      {step.summary}
                    </p>

                    <p className="mt-6 text-base leading-relaxed text-gray-700">{step.detail}</p>

                    <div className="mt-8 rounded-2xl border border-black/[0.07] bg-white p-5 shadow-[0_8px_24px_-16px_rgba(0,0,0,0.25)]">
                      <p className="mb-3 text-[11px] font-black uppercase tracking-[0.18em] text-[#9a7b00]">
                        What this includes
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {step.items.map((item) => (
                          <div key={item} className="flex gap-3 items-center text-sm font-semibold text-gray-800">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFD221]/30">
                              <CheckCircle2 size={13} className="text-[#c9a000]" />
                            </span>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3 justify-between items-center mt-8">
                      <div className="flex gap-1.5">
                        {onboardingStepsResolved.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setActiveStep(index)}
                            aria-label={`Go to step ${index + 1}`}
                            className={`h-1.5 rounded-full transition-all ${
                              index === activeStep
                                ? "w-8 bg-[#FFD221]"
                                : index < activeStep
                                ? "w-4 bg-[#c9a000]/60"
                                : "w-3 bg-black/15"
                            }`}
                          />
                        ))}
                      </div>

                      {activeStep < onboardingStepsResolved.length - 1 ? (
                        <button
                          type="button"
                          onClick={() => setActiveStep(activeStep + 1)}
                          className="inline-flex items-center gap-2 rounded-xl bg-black px-5 py-2.5 text-xs font-black text-white transition hover:bg-[#FFD221] hover:text-black"
                        >
                          Next step
                          <ChevronRight size={14} />
                        </button>
                      ) : (
                        <Link
                          to="/partners/onboard"
                          className="inline-flex items-center gap-2 rounded-xl bg-[#FFD221] px-5 py-2.5 text-xs font-black text-black transition hover:bg-[#ffdf44]"
                        >
                          Apply now
                          <Rocket size={14} />
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Lead journey */}
      <section className="px-6 py-24 text-white bg-gray-950 lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <motion.div {...fadeUp(0)}>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#FFD221]">
              Lead Quality
            </p>
            <h2 className="mt-3 text-4xl font-black leading-tight sm:text-5xl">
              Your leads arrive with context.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-gray-400">
              InfoBoxx is designed to warm up the user before the conversion.
              By the time they reach you, they have already learned, compared,
              and signaled intent.
            </p>
          </motion.div>

          <div className="space-y-4">
            {leadJourney.map((item, index) => (
              <motion.div
                key={item}
                {...fadeUp(index * 0.08)}
                className="flex gap-4 items-center p-5 rounded-2xl border backdrop-blur border-white/10 bg-white/5"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFD221] text-sm font-black text-black">
                  {index + 1}
                </span>
                <p className="font-semibold text-gray-100">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ + CTA */}
      <section className="px-6 py-24 bg-white lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_0.85fr] lg:items-start">
          <div>
            <motion.div {...fadeUp(0)} className="mb-10">
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
                Partner FAQs
              </p>
              <h2 className="mt-3 text-4xl font-black text-black sm:text-5xl">
                Questions partners ask.
              </h2>
            </motion.div>

            <div className="space-y-3">
              {faqs.map(({ q, a }, index) => (
                <motion.div
                  key={q}
                  {...fadeUp(index * 0.06)}
                  className="overflow-hidden rounded-2xl border border-black/[0.08] bg-[#f7f4ec]"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="flex gap-4 justify-between items-center px-6 py-5 w-full text-left"
                  >
                    <span className="text-sm font-black text-black">{q}</span>
                    <ChevronRight
                      size={18}
                      className={`shrink-0 text-[#c9a000] transition ${
                        openFaq === index ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <p className="border-t border-black/[0.08] px-6 pb-5 pt-4 text-sm leading-relaxed text-gray-600">
                          {a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.aside
            {...fadeUp(0.12)}
            className="sticky top-24 rounded-[2rem] border border-[#FFD221]/30 bg-gray-950 p-8 text-white shadow-2xl"
          >
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFD221] text-black">
              <Rocket size={24} />
            </div>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#FFD221]">
              Ready to own a lane?
            </p>
            <h3 className="mt-3 text-3xl font-black leading-tight">
              Become the trusted provider users meet before they decide.
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-gray-400">
              Apply now and let InfoBoxx turn your expertise into education,
              trust, subscribers, and qualified leads.
            </p>
            <div className="flex flex-col gap-3 mt-8">
              <Link
                to="/partners/onboard"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FFD221] px-7 py-4 text-sm font-black text-black transition hover:bg-[#ffdf44]"
              >
                Begin Partner Onboarding
                <ArrowRight size={16} />
              </Link>
              <a
                href={PROVIDER_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex gap-2 justify-center items-center px-7 py-4 text-sm font-black text-white rounded-2xl border transition border-white/15 bg-white/5 hover:bg-white/10"
              >
                Already a partner? Sign In
                <ArrowRight size={16} />
              </a>
              <Link
                to="/contact"
                className="inline-flex gap-2 justify-center items-center px-7 py-4 text-sm font-black text-white rounded-2xl border transition border-white/15 bg-white/5 hover:bg-white/10"
              >
                Talk to Us First
                <MessageCircle size={16} />
              </Link>
            </div>
          </motion.aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnersPage;
